<template>
  <div>
    <div class="config-section" v-if="custom">
      <PlayerConfigSourceConfig v-model="value"/>
    </div>

    <div class="config-section" v-if="custom">
      <InputTextField label="Name" v-model="value.metadata.title"
                      help="By giving this configuration a name, you can save it locally and find it on the Demos page."/>
    </div>

    <div class="config-section" v-if="!custom && url" @click="copyUrlToClipboard()">
      <label class="input-label">Content URL</label>
      <div class="content-url" title="Click to Copy">{{url}}</div>
    </div>

    <div v-if="advancedMode" class="advanced-config">
      <div class="config-section">
        <InputTextField label="Start Time (Seconds)" type="number" v-model="value.startTime"
                        :help="apiDocs('clpp.PlayerConfiguration#startTime')"/>

        <FlexRow>
          <FlexItem base="35%">
            <InputTextField label="Preferred Audio Language" v-model="value.preferredAudioLanguage"
                            :text2array="true"
                            :help="apiDocs('clpp.PlayerConfiguration#preferredAudioLanguage')"/>
          </FlexItem>
          <FlexItem base="35%">
            <InputTextField label="Preferred Text Language" v-model="value.preferredTextLanguage"
                            :text2array="true"
                            :help="apiDocs('clpp.PlayerConfiguration#preferredTextLanguage')"/>
          </FlexItem>
          <FlexItem base="30%">
            <InputCheckbox label="Prefer Forced Subtitles" v-model="value.preferForcedSubtitles"
                           :help="apiDocs('clpp.PlayerConfiguration#preferForcedSubtitles')"/>
          </FlexItem>
        </FlexRow>
        <FlexRow>
          <FlexItem>
            <InputSelect v-model="value.mediaSource.codecSwitchingStrategy" label="Codec Switching Strategy"
                     :help="apiDocs('clpp.CodecSwitchingStrategy')"
                     :options="[
                       {value: 'prevent', label: 'PREVENT'},
                       {value: 'smooth', label: 'SMOOTH'},
                     ]"/>
          </FlexItem>
          <FlexItem base="30%">
            <InputTextField label="Preferred Audio Codecs" v-model="value.preferredAudioCodec"
                            :text2array="true"
                            :help="apiDocs('clpp.PlayerConfiguration#preferredAudioCodec')"/>
          </FlexItem>
          <FlexItem base="30%">
            <InputTextField label="Preferred Video Codecs" v-model="value.preferredVideoCodec"
                            :text2array="true"
                            :help="apiDocs('clpp.PlayerConfiguration#preferredVideoCodec')"/>
          </FlexItem>
        </FlexRow>
        <FlexRow>
          <FlexItem base="50%">
            <InputCheckbox label="Prefer Spatial Audio" v-model="value.preferSpatialAudio"
                           :help="apiDocs('clpp.PlayerConfiguration#preferSpatialAudio')"/>
          </FlexItem>
        </FlexRow>

        <FlexRow>
          <FlexItem>
            <InputRange label="Volume" :min="0" :max="1.0" :null-value="1" v-model="value.volume"
                        :help="apiDocs('clpp.PlayerConfiguration#volume')"/>
          </FlexItem>
          <FlexItem>
            <InputCheckbox label="Muted" v-model="value.muted" :help="apiDocs('clpp.PlayerConfiguration#muted')"/>
          </FlexItem>
        </FlexRow>
        <FlexRow>
          <FlexItem base="20%">
            <InputCheckbox label="Auto Play" v-model="value.autoplay"
                           :help="apiDocs('clpp.PlayerConfiguration#autoplay')"/>
          </FlexItem>
          <FlexItem base="20%">
            <InputCheckbox label="Loop" v-model="value.loop" :help="apiDocs('clpp.PlayerConfiguration#loop')"/>
          </FlexItem>
          <FlexItem base="30%">
            <InputCheckbox label="Disable Audio Tracks" v-model="value.streaming.disableAudioTracks"
                           :help="apiDocs('clpp.StreamingConfiguration#disableAudioTracks')"/>
          </FlexItem>
          <FlexItem base="30%">
            <InputCheckbox label="Disable Video Tracks" v-model="value.streaming.disableVideoTracks"
                           :help="apiDocs('clpp.StreamingConfiguration#disableVideoTracks')"/>
          </FlexItem>
        </FlexRow>
        <FlexRow>
          <InputCheckbox label="Use CMSD Bandwidth Estimation"
                         v-model="value.abr.useCmsd"
                         :help="apiDocs('clpp.AbrConfiguration#useCmsd')"/>
        </FlexRow>
        <FlexRow>
          <FlexItem>
            <InputTextField label="HLS Default Codecs" v-model="value.manifest.hlsDefaultCodecs"
                            :help="apiDocs('clpp.ManifestConfiguration#hlsDefaultCodecs')"/>
          </FlexItem>
        </FlexRow>
      </div>

      <h2 class="config-header">Subtitles</h2>
      <div class="config-section">
        <InputCheckbox label="Enable HTML Cue" v-model="value.enableHtmlCue"
                       :help="apiDocs('clpp.PlayerConfiguration#enableHtmlCue')"/>
        <InputCheckbox label="Stretch subtitle images" v-model="value.htmlcue.stretchSmpteImage"
                       :help="apiDocs('clpp.HtmlCueConfiguration#stretchSmpteImage')"/>
      </div>

      <h2 class="config-header">Text Style</h2>
      <div class="config-section">
        <FlexRow>
          <FlexItem base="30%">
            <InputTextField label="Background Color" v-model="value.textStyle.backgroundColor"
              :help="apiDocs('clpp.TextStyleConfiguration#backgroundColor')" />
          </FlexItem>
          <FlexItem base="30%">
            <InputTextField label="Edge Color" v-model="value.textStyle.edgeColor"
              :help="apiDocs('clpp.TextStyleConfiguration#edgeColor')" />
          </FlexItem>
          <FlexItem base="30%">
            <InputSelect v-model="value.textStyle.edgeType" label="Edge Type"
              :help="apiDocs('clpp.text.EdgeType')"
              :options="enumToOptions('clpp.text.EdgeType')" />
          </FlexItem>
        </FlexRow>
        <FlexRow>
          <FlexItem base="30%">
            <InputTextField label="Font Family" v-model="value.textStyle.fontFamily"
              :help="apiDocs('clpp.TextStyleConfiguration#fontFamily')" />
          </FlexItem>
          <FlexItem base="30%">
            <InputTextField label="Font Size" v-model="value.textStyle.fontSize"
              :help="apiDocs('clpp.TextStyleConfiguration#fontSize')" />
          </FlexItem>
          <FlexItem base="30%">
            <InputTextField label="Font Size Percent" v-model="value.textStyle.fontSizePercent"
              :help="apiDocs('clpp.TextStyleConfiguration#fontSizePercent')" />
          </FlexItem>
        </FlexRow>
      </div>

      <h2 class="config-header">TTML</h2>
      <div class="config-section">
        <FlexRow>
          <FlexItem>
            <InputTextField label="Container width" :null2undefined="true" type="number"
                            v-model="value.ttml.containerWidth"
                            :help="apiDocs('clpp.TtmlConfiguration#containerWidth')"/>
          </FlexItem>
          <FlexItem>
            <InputTextField label="Container height" :null2undefined="true" type="number"
                            v-model="value.ttml.containerHeight"
                            :help="apiDocs('clpp.TtmlConfiguration#containerHeight')"/>
          </FlexItem>
        </FlexRow>
        <InputTextField label="Container rows" :null2undefined="true" type="number"
                        v-model="value.ttml.containerRows"
                        :help="apiDocs('clpp.TtmlConfiguration#containerRows')"/>
      </div>
      <h2 class="config-header">Additional Headers</h2>
      <div class="config-section">
        <FlexRow v-for="(header, index) in value.headers" :key="index">
          <InputTextField label="Field Name" v-model="value.headers[index].name"/>
          <InputTextField label="Field Value" v-model="value.headers[index].value"/>
          <div style="padding-top: 26px">
            <button type="button" class="button" @click="removeHeader(index)">&times;</button>
          </div>

        </FlexRow>
        <button type="button" class="button" @click="addHeader">Add Header</button>
      </div>

      <h2 class="config-header">Ads</h2>
      <div class="config-section">
        <InputTextField label="Ad Tag URL" v-model="value.ima.adTagUrl"
                        :help="apiDocs('clpp.ImaConfiguration#adTagUrl')"/>
        <InputCheckbox label="Pre-Loading" v-model="value.ima.enablePreloading"
                       :help="apiDocs('clpp.ImaConfiguration#enablePreloading')"/>

      </div>

      <h2 class="config-header">DRM</h2>
      <div class="config-section flex-row" style="padding-bottom: 2rem">
        <div style="flex: 0 0 auto">
          <Shield style="width: 30px; vertical-align: middle;"/>
        </div>
        <div style="flex: 1 1 auto; padding-left: 2rem;">
          PRESTOplay works seamlessly with our scalable <a href="https://castlabs.com/drmtoday/" target="_blank">DRMtoday</a>
          cloud licensing service.
          If you’d like to test your own protected content just request <a
          href="https://castlabs.com/free-trials/drmtoday/" target="_blank">a free DRM trial account</a>.
        </div>
      </div>
      <div class="config-section">
        <PlayerConfigDrmConfig v-model="value"/>
      </div>

      <h2 class="config-header">Live</h2>
      <div class="config-section">
        <FlexRow>
          <InputCheckbox label="Low Latency Mode" v-model="value.lowLatencyMode"
                         :help="apiDocs('clpp.PlayerConfiguration#lowLatencyMode')"/>
          <InputTextField label='Live Edge Delay' :null2undefined="true" type="number"
                          v-model="value.suggestedPresentationDelay"
                          :help="apiDocs('clpp.PlayerConfiguration#suggestedPresentationDelay')"/>
        </FlexRow>
        <FlexRow>
          <InputCheckbox label="Ignore potential Manifest vs Media Drift" v-model="value.streaming.ignoreDrift"
                         :help="apiDocs('clpp.StreamingConfiguration#ignoreDrift')"/>
        </FlexRow>
        <FlexRow>
          <InputCheckbox label="No Fragment Loading" v-model="value.streaming.disableFragmentSupport"
                         :help="apiDocs('clpp.StreamingConfiguration#disableFragmentSupport')"/>
          <InputCheckbox label="No Fragment Merging" v-model="value.streaming.disableMergedFragments"
                         :help="apiDocs('clpp.StreamingConfiguration#disableMergedFragments')"/>
        </FlexRow>

      </div>

      <h2 class="config-header">VR</h2>
      <div class="config-section">
        <FlexRow>
          <InputCheckbox label="Enable" v-model="value.vr.enable"
                         :help="apiDocs('clpp.VrConfiguration#enable')"/>
        </FlexRow>
        <FlexRow>
          <InputTextField label="Field of View" type="number" v-model="value.vr.fieldOfView"
                          :help="apiDocs('clpp.VrConfiguration#fieldOfView')"/>
          <InputTextField label="Controls Sensitivity" type="number" v-model="value.vr.sensitivity"
                          :help="apiDocs('clpp.VrConfiguration#sensitivity')"/>
        </FlexRow>
        <FlexRow>
          <InputCheckbox label="Invert Horizontal Control" v-model="value.vr.invertHorizontalControl"
                         :help="apiDocs('clpp.VrConfiguration#invertHorizontalControl')"/>
          <InputCheckbox label="Invert Vertical Control" v-model="value.vr.invertVerticalControl"
                         :help="apiDocs('clpp.VrConfiguration#invertVerticalControl')"/>
        </FlexRow>
      </div>

      <h2 class="config-header">Broadpeak</h2>
      <div class="config-section">
        <PlayerConfigBroadpeakConfig v-model="value"/>
      </div>

      <h2 class="config-header">Thumbnails</h2>
      <div class="config-section">
        <PlayerConfigThumbnailConfig v-model="value"/>
      </div>

      <h2 class="config-header">Streaming Configuration</h2>
      <div class="config-section">
        <FlexRow>
          <FlexItem>
            <InputTextField label="Buffering Goal" type="number" v-model="value.streaming.bufferingGoal"
                            :help="apiDocs('clpp.StreamingConfiguration#bufferingGoal')"/>
          </FlexItem>
          <FlexItem>
            <InputTextField label="Buffer Behind" type="number" v-model="value.streaming.bufferBehind"
                            :help="apiDocs('clpp.StreamingConfiguration#bufferBehind')"/>
          </FlexItem>
        </FlexRow>
        <InputTextField label="Re-Buffering Goal" type="number" v-model="value.streaming.rebufferingGoal"
                        :help="apiDocs('clpp.StreamingConfiguration#rebufferingGoal')"/>
        <FlexRow>
          <FlexItem>
            <InputTextField label="Small Gap Limit" type="number" v-model="value.streaming.smallGapLimit"
                            :help="apiDocs('clpp.StreamingConfiguration#smallGapLimit')"/>
          </FlexItem>
          <FlexItem :grow="0" base="50%" class="space-left">
            <InputCheckbox label="Jump Large Gaps" v-model="value.streaming.jumpLargeGaps"
                           :help="apiDocs('clpp.StreamingConfiguration#jumpLargeGaps')"/>
          </FlexItem>
        </FlexRow>
        <InputTextField label="Duration Backoff" type="number" v-model="value.streaming.durationBackoff"
                        :help="apiDocs('clpp.StreamingConfiguration#durationBackoff')"/>
        <FlexRow>
          <FlexItem :grow="0" base="50%">
            <InputCheckbox label="Ignore Text Stream Failures" v-model="value.streaming.ignoreTextStreamFailures"
                           :help="apiDocs('clpp.StreamingConfiguration#ignoreTextStreamFailures')"/>
          </FlexItem>
          <FlexItem :grow="0" base="50%" class="space-left">
            <InputCheckbox label="Always Stream Text" v-model="value.streaming.alwaysStreamText"
                           :help="apiDocs('clpp.StreamingConfiguration#alwaysStreamText')"/>
          </FlexItem>
        </FlexRow>
        <FlexRow>
          <FlexItem :grow="0" base="50%">
            <InputCheckbox label="Start at Segment Boundary" v-model="value.streaming.startAtSegmentBoundary"
                           :help="apiDocs('clpp.StreamingConfiguration#startAtSegmentBoundary')"/>
          </FlexItem>
          <FlexItem :grow="0" base="50%" class="space-left">
            <InputCheckbox label="Override PASP values" v-model="value.streaming.overridePasp"
                           :help="apiDocs('clpp.StreamingConfiguration#overridePasp')"/>
          </FlexItem>
        </FlexRow>
        <FlexRow>
          <InputCheckbox label="Ignore Managed Media Source (MMS) streaming hints"
                         v-model="value.streaming.ignoreManagedMediaSourceStreamingHints"
                         :help="apiDocs('clpp.StreamingConfiguration#ignoreManagedMediaSourceStreamingHints')"/>
          <InputCheckbox label="Prefer native HLS on Safari" v-model="value.streaming.preferNativeHlsOnSafari"
                         :help="apiDocs('clpp.StreamingConfiguration#preferNativeHlsOnSafari')"/>
        </FlexRow>
      </div>

      <h2 class="config-header">Resolution Restrictions</h2>
      <div class="config-section">
        <FlexRow>
          <FlexItem base="50%">
            <InputTextField label="Min Width" type="number" v-model="value.restrictions.minWidth"
                            :help="apiDocs('clpp.Restrictions#minWidth')"/>
          </FlexItem>
          <FlexItem base="50%">
            <InputTextField label="Max Width" type="number" v-model="value.restrictions.maxWidth"
                            :help="apiDocs('clpp.Restrictions#maxWidth')"/>
          </FlexItem>
        </FlexRow>
        <FlexRow>
          <FlexItem base="50%">
            <InputTextField label="Min Height" type="number" v-model="value.restrictions.minHeight"
                            :help="apiDocs('clpp.Restrictions#minHeight')"/>
          </FlexItem>
          <FlexItem base="50%">
            <InputTextField label="Max Height" type="number" v-model="value.restrictions.maxHeight"
                            :help="apiDocs('clpp.Restrictions#maxHeight')"/>
          </FlexItem>
        </FlexRow>
        <FlexRow>
          <FlexItem base="50%">
            <InputTextField label="Min Bandwidth" type="number" v-model="value.restrictions.minBandwidth"
                            :help="apiDocs('clpp.Restrictions#minBandwidth')"/>
          </FlexItem>
          <FlexItem base="50%">
            <InputTextField label="Max Bandwidth" type="number" v-model="value.restrictions.maxBandwidth"
                            :help="apiDocs('clpp.Restrictions#maxBandwidth')"/>
          </FlexItem>
        </FlexRow>
        <FlexRow>
          <FlexItem base="50%">
            <InputTextField label="Min Pixels" type="number" v-model="value.restrictions.minPixels"
                            :help="apiDocs('clpp.Restrictions#minPixels')"/>
          </FlexItem>
          <FlexItem base="50%">
            <InputTextField label="Max Pixels" type="number" v-model="value.restrictions.maxPixels"
                            :help="apiDocs('clpp.Restrictions#maxPixels')"/>
          </FlexItem>
        </FlexRow>
      </div>

      <h2 class="config-header">Common Media Client Data (CMCD)</h2>
      <div class="config-section">
        <FlexRow>
          <FlexItem base="23%">
            <InputCheckbox label="Enable CMCD"
                           v-model="value.cmcd.enabled"
                           :help="apiDocs('clpp.CmcdConfiguration#enabled')"/>
          </FlexItem>
          <FlexItem base="23%">
            <InputTextField label="CMCD Version"
                            type="number"
                            :null2undefined="true"
                            v-model="value.cmcd.version"
                            :help="apiDocs('clpp.CmcdConfiguration#version')"/>
          </FlexItem>
          <FlexItem base="23%">
            <InputTextField label="RTP Safety Factor"
                            type="number"
                            :null2undefined="true"
                            v-model="value.cmcd.rtpMultiplier"
                            :help="apiDocs('clpp.CmcdConfiguration#rtpMultiplier')"/>
          </FlexItem>
          <FlexItem base="23%">
            <InputSelect label="Delivery Mode"
                         v-model="value.cmcd.deliveryMode"
                         :options="cmcdDeliveryModeOptions"
                         :help="apiDocs('clpp.CmcdConfiguration#deliveryMode')"/>
          </FlexItem>
        </FlexRow>
        <FlexRow>
          <FlexItem base="50%">
            <InputTextField label="Session ID"
                            v-model="value.cmcd.sessionId"
                            :null2undefined="true"
                            :help="apiDocs('clpp.CmcdConfiguration#sessionId')"/>
          </FlexItem>
          <FlexItem base="50%">
            <InputTextField label="Content ID"
                            v-model="value.cmcd.contentId"
                            :null2undefined="true"
                            :help="apiDocs('clpp.CmcdConfiguration#contentId')"/>
          </FlexItem>
        </FlexRow>
      </div>

      <h2 class="config-header">Chromecast</h2>
      <div class="config-section">
        <InputTextField label="Chromecast Receiver Application ID" v-model="value.CCReceiverId" />
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import PlayerConfigSourceConfig from './PlayerConfigSourceConfig';
import PlayerConfigBroadpeakConfig from './PlayerConfigBroadpeakConfig';
import InputTextField from '../../forms/InputTextField';
import { Docs } from '../../../utils/docs';
import FlexRow from '../../layout/FlexRow';
import FlexItem from '../../layout/FlexItem';
import InputRange from '../../forms/InputRange';
import InputCheckbox from '../../forms/InputCheckbox';
import InputSelect from '../../forms/InputSelect';
import PlayerConfigDrmConfig from './PlayerConfigDrmConfig';
import PlayerConfigThumbnailConfig from './PlayerConfigThumbnailConfig';
import Shield from '../../../assets/shield.svg';
import { configToUrl } from '../../../utils/player';

export default {
  name: 'PlayerConfigBasicConfig',
  components: {
    PlayerConfigBroadpeakConfig,
    PlayerConfigThumbnailConfig,
    PlayerConfigDrmConfig,
    InputCheckbox,
    InputRange,
    InputSelect,
    FlexItem,
    FlexRow,
    InputTextField,
    PlayerConfigSourceConfig,
    Shield
  },
  props: ['value', 'advancedMode', 'custom'],
  computed: {
    url () {
      return configToUrl(this.value);
    },
    cmcdDeliveryModeOptions () {
      return [
        { value: 'headers', label: 'Headers' },
        { value: 'query', label: 'Query' },
      ];
    }
  },
  methods: {
    async copyUrlToClipboard () {
      try {
        await navigator.clipboard.writeText(this.url);
        Vue.$toast.open({
          message: 'URL copied to clipboard.',
          duration: 2000,
          position: 'top',
        });
      } catch (err) {
        console.error('Failed to copy to clipboard', err);
      }
    },
    apiDocs (field) {
      return Docs.resolve(Docs.getDescription(field));
    },
    enumToOptions (field) {
      const docsEntry = Docs.getEntryByKey(field);
      if (!docsEntry || !docsEntry.isEnum || !docsEntry.properties) {
        return [];
      }

      return docsEntry.properties.map(({ name, defaultvalue }) => ({
        value: defaultvalue,
        label: name,
      }));
    },
    addHeader () {
      if (!Array.isArray(this.value.headers)) {
        this.$set(this.value, 'headers', []);
      }
      this.value.headers.push({
        name: '',
        value: ''
      });
      console.log(this.value.headers);
      this.$set(this.value, 'headers', this.value.headers);
    },
    removeHeader (index) {
      this.value.headers.splice(index, 1);
      this.$set(this.value, 'headers', this.value.headers);
    },
  }
};
</script>

<style scoped lang="scss">
.advanced-config {
  @import "src/style/color_";

  border-top: 1px solid $color-tertiary;
  padding-top: 4rem;
  margin-top: 4rem;
}

.content-url {
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 10px;
}

</style>
